import { z } from "zod";

export function createTypedDataValueSchema<T extends z.ZodTypeAny>(
  withType: T,
) {
  return z.object({
    value: withType,
    nativeValue: withType,
    formattedValue: z.string(),
    originalFieldName: z.string(),
  });
}

type UnpackZodObject<T> = T extends z.ZodObject<infer U> ? U : never;
type TransformObjectToDataValueSchema<T> = {
  [K in keyof T]: T[K] extends z.ZodTypeAny
    ? ReturnType<typeof createTypedDataValueSchema<T[K]>>
    : T[K];
};

export function transformToTypedDataValueSchema<
  T extends z.ZodObject<{ [k: string]: z.ZodTypeAny }>,
>(schema: T) {
  const shape = schema.shape;

  const newShape: {
    [K in keyof typeof shape]: (typeof shape)[K] extends z.ZodTypeAny
      ? ReturnType<typeof createTypedDataValueSchema<(typeof shape)[K]>>
      : (typeof shape)[K];
  } = Object.fromEntries(
    Object.entries(shape).map(([key, value]) => [
      key,
      value instanceof z.ZodType ? createTypedDataValueSchema(value) : value,
    ]),
  );

  return z.object(newShape) as z.ZodObject<
    TransformObjectToDataValueSchema<UnpackZodObject<T>>
  >;
}

export type PropertyValuesByKey<
  S extends string,
  T extends Record<string, { [K in S]: unknown }>,
> = {
  [K in keyof T]: T[K][S];
};

export function extractPropertyValues<
  S extends string,
  T extends Record<string, { [K in S]: unknown }>,
>(obj: T, property: S): PropertyValuesByKey<S, T> {
  return Object.entries(obj).reduce<PropertyValuesByKey<S, T>>(
    (acc, [key, obj]) => {
      return {
        ...acc,
        [key]: obj[property],
      };
    },
    {} as PropertyValuesByKey<S, T>,
  );
}
